import { ensureRouteData } from '@/app/query-client'
import { newsletterIssueQueryOptions } from '@/features/newsletter/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { parseIssueNumber } from '@/screens/newsletter/lib/parseIssueNumber'

/**
 * The `/newsletters/$issueNumber` loader.
 *
 * MAIN, awaited: the issue itself — the header's own title and every page
 * card come from it, so there is nothing on this route a card could fail for
 * on its own. `newsletterIssueQueryOptions` resolves `NEWSLETTER_NOT_FOUND`
 * to `null` rather than throwing (see its own doc), so this only ever
 * rejects — and only ever throws the route to its `errorComponent` — for a
 * genuine failure, never a missing issue.
 *
 * A param that is not a positive integer never reaches the network at all:
 * `parseIssueNumber` answers `null` without a request, and this loader
 * returns immediately rather than asking `/api/v5/newsletters/NaN/` a
 * question it cannot answer. `NewsletterIssueRoute` (the screen's own file)
 * makes the same check before it ever mounts the suspense-reading screen.
 *
 * Its own module, as `app.guard.ts` is, so a test can mount it under a root
 * of its own: typed inside `createFileRoute`, a loader is bound to this
 * route's parent in the generated tree. It imports from `api/` and `lib/`
 * modules only — route loaders are not code-split, so anything imported
 * here ships in the entry chunk.
 */
export async function loadNewsletterIssue({
  context: { queryClient },
  params,
}: {
  context: RouterContext
  params: { issueNumber: string }
}): Promise<void> {
  const issueNumber = parseIssueNumber(params.issueNumber)
  if (issueNumber === null) return
  await ensureRouteData(queryClient, newsletterIssueQueryOptions(issueNumber))
}
